import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Alert,
  Space,
  Typography,
  message,
  Divider
} from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { inventoryAPI } from '../services/api.ts';

const { TextArea } = Input;
const { Text } = Typography;

interface UdcChangeModalProps {
  visible: boolean;
  stockId: number | null;
  productDescription?: string;
  currentUdcPallet?: string;
  currentUdcCollo?: string;
  availableQuantity?: number;
  onOk: () => void;
  onCancel: () => void;
}

const UdcChangeModal: React.FC<UdcChangeModalProps> = ({
  visible,
  stockId,
  productDescription,
  currentUdcPallet,
  currentUdcCollo,
  availableQuantity = 0,
  onOk,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      await form.validateFields();

      if (!stockId) return;

      setLoading(true);

      const values = form.getFieldsValue();

      // Validazione: UDC destinazione devono essere diversi da quelli correnti
      const palletSame = values.destination_udc_pallet?.trim() === currentUdcPallet;
      const colloSame = values.destination_udc_collo?.trim() === currentUdcCollo;

      if (palletSame && colloSame) {
        message.warning('Gli UDC di destinazione devono essere diversi da quelli correnti');
        setLoading(false);
        return;
      }

      const transferData = {
        quantity: values.quantity,
        destination_udc_pallet: values.destination_udc_pallet?.trim(),
        destination_udc_collo: values.destination_udc_collo?.trim(),
        reason: values.reason,
        notes: values.notes
      };

      await inventoryAPI.transferStockToUdc(stockId, transferData);

      message.success('Quantità spostata con successo');
      form.resetFields();
      onOk();
    } catch (error: any) {
      console.error('Errore nello spostamento:', error);
      message.error(error.response?.data?.detail || 'Errore durante lo spostamento');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          <SwapOutlined />
          <span>Sposta tra UDC</span>
        </Space>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Sposta Quantità"
      cancelText="Annulla"
      width={600}
    >
      <Divider />

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          message={
            <Space direction="vertical" size="small">
              <Text strong>Prodotto: {productDescription}</Text>
              <Text strong style={{ color: '#1890ff' }}>
                Quantità Disponibile: {availableQuantity}
              </Text>
              <Text>UDC Pallet Corrente: <strong>{currentUdcPallet || 'N/A'}</strong></Text>
              <Text>UDC Collo Corrente: <strong>{currentUdcCollo || 'N/A'}</strong></Text>
            </Space>
          }
          type="info"
        />

        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="quantity"
            label="Quantità da Spostare"
            rules={[
              { required: true, message: 'La quantità è obbligatoria' },
              { type: 'number', min: 1, message: 'La quantità deve essere almeno 1' },
              {
                validator: async (_, value) => {
                  if (value && value > availableQuantity) {
                    throw new Error(`La quantità massima disponibile è ${availableQuantity}`);
                  }
                }
              }
            ]}
          >
            <InputNumber
              min={1}
              max={availableQuantity}
              style={{ width: '100%' }}
              placeholder={`Max: ${availableQuantity}`}
            />
          </Form.Item>

          <Form.Item
            name="destination_udc_pallet"
            label="UDC Pallet Destinazione"
            rules={[
              { required: true, message: 'UDC Pallet destinazione è obbligatorio' },
              { whitespace: true, message: 'UDC Pallet non può essere vuoto' },
              { max: 100, message: 'UDC Pallet troppo lungo (max 100 caratteri)' }
            ]}
          >
            <Input
              placeholder="Inserisci codice UDC Pallet di destinazione"
              maxLength={100}
            />
          </Form.Item>

          <Form.Item
            name="destination_udc_collo"
            label="UDC Collo Destinazione"
            rules={[
              { required: true, message: 'UDC Collo destinazione è obbligatorio' },
              { whitespace: true, message: 'UDC Collo non può essere vuoto' },
              { max: 100, message: 'UDC Collo troppo lungo (max 100 caratteri)' }
            ]}
          >
            <Input
              placeholder="Inserisci codice UDC Collo di destinazione"
              maxLength={100}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="Motivo Spostamento"
            rules={[
              { required: true, message: 'Il motivo è obbligatorio' },
              { max: 500, message: 'Motivo troppo lungo (max 500 caratteri)' },
              { whitespace: true, message: 'Il motivo non può essere vuoto' }
            ]}
          >
            <Input
              placeholder="Es: Riorganizzazione UDC, Consolidamento colli, ecc."
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Note Aggiuntive (opzionale)"
            rules={[
              { max: 500, message: 'Note troppo lunghe (max 500 caratteri)' }
            ]}
          >
            <TextArea rows={3} placeholder="Eventuali note sullo spostamento..." />
          </Form.Item>
        </Form>

        <Alert
          message="La quantità specificata verrà spostata agli UDC di destinazione. Se la destinazione non esiste, verrà creata automaticamente una nuova riga. L'operazione verrà registrata nella storia dei movimenti."
          type="warning"
          showIcon
        />
      </Space>
    </Modal>
  );
};

export default UdcChangeModal;
